import type { FunctionComponent } from 'preact';

import type { RepeaterFunctionInvoker, TemplateFunction, TemplateResult } from 'Components/types';
import { garageCleanupRequested, removalFromGarageRequested } from 'Core/actions/fitmentSearch';
import {
  garageDataSelector,
  garageEmptySelector,
  garageSizeSelector,
  isVehicleSelectedSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch';
import { useDispatch, useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import type { VehicleFieldMap } from 'Models/vehicle';

import type { VehicleListItemParams } from '../types';
import VehicleListItem from './vehicleListItem';

type Props = {
  close?: () => void;
  doNotRedirectOnVehicleSelect?: boolean;
  redirectUrl?: string;
  template: TemplateFunction<VehicleListParams>;
};

export type VehicleListParams = {
  clearGarage: (e?: Event) => void;
  items: RepeaterFunctionInvoker<VehicleListItemParams>;
  isEmpty: boolean;
  isVehicleSelected: boolean;
  removeVehicle: (e?: Event) => void;
  size: number;
  vehicle: VehicleFieldMap;
};

const VehicleList: FunctionComponent<Props> = ({
  close,
  doNotRedirectOnVehicleSelect,
  redirectUrl,
  template,
}): TemplateResult => {
  const dispatch = useDispatch();

  const garageData = useSelector(garageDataSelector);
  const isEmpty = useSelector(garageEmptySelector);
  const isVehicleSelected = useSelector(isVehicleSelectedSelector);
  const selectedVehicle = useSelector(selectedVehicleSelector);
  const size = useSelector(garageSizeSelector);

  const items: RepeaterFunctionInvoker<VehicleListItemParams> = garageData.map(
    (vehicle) => (itemTemplate) => (
      <VehicleListItem
        close={close}
        doNotRedirectOnVehicleSelect={doNotRedirectOnVehicleSelect}
        redirectUrl={redirectUrl}
        template={itemTemplate}
        vehicle={vehicle}
        key={vehicle.key}
      />
    ),
  );

  const clearGarage = (e?: Event) => {
    e?.stopPropagation();
    dispatch(garageCleanupRequested(isVehicleSelected));
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const removeVehicle = (e?: Event) => {
    e?.stopPropagation();
    dispatch(removalFromGarageRequested(selectedVehicle, isVehicleSelected));
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const component = template.call({
    clearGarage,
    items,
    isEmpty,
    isVehicleSelected,
    removeVehicle,
    size,
    vehicle: selectedVehicle.filteredFieldMap,
  });

  return component;
};

export default VehicleList;
